import { validateDateRange } from '../../../../../_metronic/helpers'
import {
  ArquivoRelatorio,
  FiltroCeae,
  FiltroRelatorios,
  ParametrosAnexoIi,
  ParametrosPeriodo,
  RatcRegistro,
  SituacaoAnexoIi,
} from './_models'

/** Limite de período das rotas de relatório (dataInicio/dataFim inclusivos). */
export const MAX_DIAS_PERIODO_RELATORIO = 366

/** AAAA-MM-DD a partir dos componentes locais — `toISOString()` deslocaria o dia no fuso do Brasil. */
const dataLocal = (data: Date) =>
  `${data.getFullYear()}-${`${data.getMonth() + 1}`.padStart(
    2,
    '0'
  )}-${`${data.getDate()}`.padStart(2, '0')}`

/**
 * Filtro inicial: período = mês corrente até hoje; competência do Anexo II = mês anterior (o
 * relatório mensal é enviado depois de o mês fechar).
 */
export const montaFiltroInicial = (hoje = new Date()): FiltroRelatorios => {
  const mesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)
  return {
    dataInicio: dataLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
    dataFim: dataLocal(hoje),
    ceae: undefined,
    competencia: dataLocal(mesAnterior).slice(0, 7),
    agrupar: 'especialidade',
  }
}

/** `ceae` da API: ausente = todas as unidades. */
export const ceaeParametro = (ceae?: FiltroCeae): boolean | undefined =>
  ceae === 'ceae' ? true : ceae === 'nao-ceae' ? false : undefined

export const erroPeriodo = (filtro: FiltroRelatorios): string =>
  validateDateRange(filtro.dataInicio, filtro.dataFim, {
    maxRangeDays: MAX_DIAS_PERIODO_RELATORIO,
  }).error

/** Parâmetros de período, ou `undefined` enquanto faltar empresa ou o período for inválido. */
export const parametrosPeriodo = (filtro: FiltroRelatorios): ParametrosPeriodo | undefined => {
  if (!filtro.empresa || !filtro.dataInicio || !filtro.dataFim || erroPeriodo(filtro)) {
    return undefined
  }
  const ceae = ceaeParametro(filtro.ceae?.value)
  return {
    empresa: filtro.empresa.value,
    dataInicio: filtro.dataInicio,
    dataFim: filtro.dataFim,
    ...(ceae !== undefined && { ceae }),
  }
}

/** Competência no formato AAAA-MM (o backend devolve 422 fora dele). */
export const competenciaValida = (competencia: string) =>
  /^\d{4}-(0[1-9]|1[0-2])$/.test(competencia)

export const parametrosAnexoIi = (filtro: FiltroRelatorios): ParametrosAnexoIi | undefined => {
  if (!filtro.empresa || !competenciaValida(filtro.competencia)) return undefined
  const ceae = ceaeParametro(filtro.ceae?.value)
  return {
    empresa: filtro.empresa.value,
    competencia: filtro.competencia,
    ...(ceae !== undefined && { ceae }),
  }
}

const numero = (valor: number, casas = 1) =>
  valor.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: casas })

export const formatarContagem = (valor?: number | null) =>
  valor === undefined || valor === null ? '—' : numero(valor, 0)

/** Horas com uma casa ("12,5 h"); sem valor (nenhuma resposta no período): "—". */
export const formatarHoras = (valor?: number | null) =>
  valor === undefined || valor === null ? '—' : `${numero(valor)} h`

/** Nota de satisfação (1 a 5) com uma casa; sem avaliações: "—". */
export const formatarNota = (valor?: number | null) =>
  valor === undefined || valor === null ? '—' : numero(valor)

export const ROTULO_SITUACAO_ANEXO_II: Record<SituacaoAnexoIi, string> = {
  [SituacaoAnexoIi.Respondida]: 'Respondida',
  [SituacaoAnexoIi.Cancelada]: 'Cancelada',
  [SituacaoAnexoIi.Devolvida]: 'Devolvida',
  [SituacaoAnexoIi.EmAndamento]: 'Em andamento',
}

/**
 * Registro RATC individual como arquivo JSON indentado, nomeado pelo protocolo
 * (`teleconsultoria.identificador`); sem protocolo, usa o id do atendimento.
 */
export const arquivoRatc = (registro: RatcRegistro): ArquivoRelatorio => {
  const protocolo = registro.teleconsultoria?.identificador || String(registro.id)
  return {
    blob: new Blob([JSON.stringify(registro, null, 2)], { type: 'application/json' }),
    nomeArquivo: `ratc-${protocolo.replace(/[^\w.-]+/g, '_')}.json`,
  }
}
